/**
 * SVG icon strings — the widget's only icon set (the React icon components
 * they were transliterated from are gone with the iframe rehost). Markup and
 * sizing match what the shared stylesheet expects.
 */

const STROKE_ATTRS =
  'fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"';

/**
 * Filled PTT microphone (components/icons/MicrophoneIcon), always
 * currentColor: the stylesheet's `.hold-to-talk-button.active svg` rule colors
 * it lime while holding. (Deliberate divergence from the React icon, whose
 * hardcoded active-blue fill overrode the lime theme and flashed on press.)
 */
export function microphoneIcon(size = 24): string {
  return (
    `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">` +
    `<path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" fill="currentColor"/>` +
    `<path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" fill="currentColor"/>` +
    `</svg>`
  );
}

/** Crossed-out microphone (components/icons/MicOffIcon). */
export function micOffIcon(size = 24): string {
  return (
    `<svg width="${size}" height="${size}" viewBox="0 0 24 24" ${STROKE_ATTRS} xmlns="http://www.w3.org/2000/svg">` +
    `<line x1="1" y1="1" x2="23" y2="23"/>` +
    `<path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"/>` +
    `<path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"/>` +
    `<line x1="12" y1="19" x2="12" y2="23"/>` +
    `</svg>`
  );
}

/** Stroke-style microphone used by the welcome/degraded button. */
export function strokeMicIcon(size = 24): string {
  return (
    `<svg width="${size}" height="${size}" viewBox="0 0 24 24" ${STROKE_ATTRS}>` +
    `<path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>` +
    `<path d="M19 10v2a7 7 0 0 1-14 0v-2"/>` +
    `<line x1="12" y1="19" x2="12" y2="23"/>` +
    `</svg>`
  );
}

/** Cancel X (components/icons/CloseIcon, active red). */
export function closeIcon(size = 24): string {
  return (
    `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">` +
    `<path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" fill="#ef4444"/>` +
    `</svg>`
  );
}

/** WhatsApp-style swipe-cancel chevron (HoldToTalkButton.ChevronUpIcon). */
export function chevronUpIcon(size = 16): string {
  return (
    `<svg width="${size}" height="${size}" viewBox="0 0 24 24" ${STROKE_ATTRS}>` +
    `<path d="M18 15l-6-6-6 6"/>` +
    `</svg>`
  );
}

/** Keyboard glyph at the pill's right end: the pill takes typed messages. */
export function keyboardIcon(size = 18): string {
  return (
    `<svg width="${size}" height="${size}" viewBox="0 0 24 24" ${STROKE_ATTRS}>` +
    `<rect x="3" y="6" width="18" height="12" rx="2"/>` +
    `<path d="M6 10h.01M9 10h.01M12 10h.01M15 10h.01M18 10h.01M7 13h.01M10 13h.01M13 13h.01M16 13h.01M8 16h8"/>` +
    `</svg>`
  );
}

/** Send arrow: the PTT button while the composer holds a draft. */
export function sendIcon(size = 24): string {
  return (
    `<svg width="${size}" height="${size}" viewBox="0 0 24 24" ${STROKE_ATTRS}>` +
    `<path d="M5 12h13M13 7l5 5-5 5"/>` +
    `</svg>`
  );
}

/** Welcome-CTA play triangle (sized by .welcome-cta-bar svg CSS). */
export function playIcon(): string {
  return `<svg viewBox="0 0 24 24" fill="currentColor" stroke="none"><path d="M7 4v16l13-8z"/></svg>`;
}

/** Degraded-state warning triangle. */
export function warnIcon(size = 15): string {
  return (
    `<svg width="${size}" height="${size}" viewBox="0 0 24 24" ${STROKE_ATTRS}>` +
    `<path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>` +
    `<line x1="12" y1="9" x2="12" y2="13"/>` +
    `<line x1="12" y1="17" x2="12.01" y2="17"/>` +
    `</svg>`
  );
}
